import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faChevronDown } from '@fortawesome/free-solid-svg-icons';
import { Link } from 'react-router-dom';
import { useMedia } from '../context/MediaContext';
import { useCopy } from '../hooks/useCopy';
import { useTranslation } from 'react-i18next';
import { useLocale } from '../context/LocaleContext';
import SEO from '../components/SEO';

type FAQItem = {
  question: string;
  answer: string;
};

const About: React.FC = () => {
  const { t } = useTranslation();
  const { localePath } = useLocale();
  const { text } = useCopy('about');
  const { getMediaById, loading: mediaLoading } = useMedia();
  const [openFaqIndex, setOpenFaqIndex] = useState<number | null>(null);

  const aboutHero = mediaLoading ? 'camilaparente.JPG' : (getMediaById('about-hero')?.cloudinaryURL || 'camilaparente.JPG');

  const blurVariants = {
    hidden: { filter: 'blur(0px)' },
    visible: { filter: 'blur(5px)', transition: { delay: 0.33, duration: 0.25 } }
  };

  const textVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: { opacity: 1, y: 0, transition: { delay: 0.7, duration: 0.25 } }
  };

  const values = [
    {
      title: text('about-value-1-title', "Ethical Craftsmanship"),
      description: text('about-value-1-desc', "Precious metals, no conflict materials.")
    },
    {
      title: text('about-value-2-title', "Artistry & Design"),
      description: text('about-value-2-desc', "Roman technique meets Mexican tradition.")
    },
    {
      title: text('about-value-3-title', "Nature & Organic Form"),
      description: text('about-value-3-desc', "Shapes drawn from the natural world.")
    },
    {
      title: text('about-value-4-title', "Self-Expression"),
      description: text('about-value-4-desc', "One-of-a-kind pieces, personal style.")
    }
  ];

  const faqs: FAQItem[] = [
    {
      question: text('about-faq-1-q', "Where are you based?"),
      answer: text('about-faq-1-a', "Our creative workshop is nestled in the heart of Rome, Italy, where each piece is designed with passion and precision.")
    },
    {
      question: text('about-faq-2-q', "Do you ship overseas?"),
      answer: text('about-faq-2-a', "We ship across the EU and offer select pieces to Mexico. For custom pieces outside these regions, we can arrange personalized shipping.")
    },
    {
      question: text('about-faq-3-q', "How long does shipping take?"),
      answer: text('about-faq-3-a', "Pieces typically arrive within 5 to 7 days across the EU. For custom arrangements, shipping times may vary.")
    },
    {
      question: text('about-faq-4-q', "Can I order a custom piece?"),
      answer: text('about-faq-4-a', "Absolutely! Custom pieces are the essence of our artistry. Simply email us or drop a DM on Instagram, and let's craft something extraordinary together.")
    },
    {
      question: text('about-faq-5-q', "What materials do you work with?"),
      answer: text('about-faq-5-a', "Our collections feature silver and golden-plated brass. For custom projects, we also work with gold to bring your unique vision to life.")
    },
    {
      question: text('about-faq-6-q', "Do you have eco-friendly options?"),
      answer: text('about-faq-6-a', "Many of our designs use recycled metals and ethically sourced materials. Custom orders can also incorporate eco-friendly choices.")
    },
    {
      question: text('about-faq-7-q', "What inspires your designs?"),
      answer: text('about-faq-7-a', "Each piece draws from mythical creatures, enigmatic textures, and timeless themes — jewelry for those who embrace the unusual.")
    }
  ];

  return (
    <>
    <SEO
      title={t('seo.about.title')}
      description={t('seo.about.description')}
    />
    <div className="w-screen">
      {/* Hero */}
      <section className="relative">
        <motion.div
          className="absolute top-0 left-1/2 -translate-x-1/2 min-w-[100vw] h-[calc(100%+89px)] -mt-[89px] bg-cover bg-left md:bg-center scale-110 -z-10"
          style={{ backgroundImage: "url('/Maira.jpg')",
              backgroundPosition: '90% %', // x y — adjust to move the image
              backgroundSize: 'cover',
              backgroundRepeat: 'no-repeat'
           }}
          initial="hidden"
          animate="visible"
          variants={blurVariants}
        >
          <div className="absolute inset-0 bg-gradient-to-b from-white/50 to-transparent dark:from-black/50" />
        </motion.div>

        <div className="relative py-14 md:py-20 px-6 md:px-4">
          <div className="max-w-[90vw] md:max-w-[min(80vw,1400px)] mx-auto">
            <motion.h1
              className="text-4xl md:text-6xl font-bold text-dark dark:text-white drop-shadow-[0_2px_4px_rgba(0,0,0,0.1)] dark:drop-shadow-[0_2px_4px_rgba(0,0,0,0.4)]"
              initial="hidden"
              animate="visible"
              variants={textVariants}
            >
              {text('about-hero-title', 'About')}
            </motion.h1>
            <motion.p
              className="mt-4 text-lg md:text-xl text-dark dark:text-white max-w-xl drop-shadow-[0_2px_4px_rgba(0,0,0,0.4)] dark:drop-shadow-[0_2px_4px_rgba(0,0,0,0.5)]"
              initial="hidden"
              animate="visible"
              variants={textVariants}
            >
              {text('about-hero-subtitle', "A jewelry project that embraces the unusual, rooted in Mexico's heritage and refined in Rome.")}
            </motion.p>
          </div>
        </div>
      </section>

      {/* Content */}
      <div>
        {/* Designer Introduction */}
        <section className="py-14 md:py-20 px-6 md:px-4">
          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.3 }}
            className="max-w-[90vw] md:max-w-[min(80vw,1400px)] mx-auto grid md:grid-cols-[1fr_1.5fr] gap-8 md:gap-12 items-start"
          >
            <div className="overflow-hidden rounded-xl shadow-lg">
              <img
                src={aboutHero}
                alt="Camila Parente"
                className="w-full h-auto object-cover"
              />
            </div>
            <div className="space-y-5 md:space-y-6">
              <h2 className="text-2xl md:text-3xl font-semibold text-black dark:text-white">
                {text('about-bio-name', 'Camila Parente')}
              </h2>
              <p className="text-base md:text-lg text-black/90 dark:text-white/90 leading-relaxed">
                {text('about-bio-paragraph-1', "Born in Mexico and trained at Rome's prestigious Scuola di Oreficeria, I create jewelry that sits at the intersection of two rich craft traditions. Each piece carries the bold, expressive spirit of Mexican metalwork alongside the refined techniques of Italian goldsmithing.")}
              </p>
              <p className="text-base md:text-lg text-black/90 dark:text-white/90 leading-relaxed">
                {text('about-bio-paragraph-2', 'My designs are inspired by mythical creatures, organic textures, and the beauty of imperfection. I work primarily in sterling silver and golden-plated brass, sourcing recycled metals whenever possible. For custom commissions, I also work with gold.')}
              </p>
              <p className="text-base md:text-lg text-black/90 dark:text-white/90 leading-relaxed">
                {text('about-bio-paragraph-3', "Every piece is handcrafted in my Rome workshop — from initial wax carving and mold-making through casting, polishing, and finishing. No two pieces are identical. The process typically takes 2–4 weeks, and I welcome visitors to the workshop by appointment.")}
              </p>
              <div className="pt-2">
                <Link
                  to={localePath('/shop')}
                  className="inline-block bg-brand-blue text-white text-base md:text-lg py-2.5 px-6 rounded-lg hover:bg-brand-dark transition-colors"
                >
                  {t('about.shopCollection')}
                </Link>
              </div>
            </div>
          </motion.div>
        </section>

        {/* Image break — full width */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.8, delay: 0.4 }}
          className="w-full h-[50vh] md:h-[50vh] overflow-hidden"
        >
          <img
            src="/img_7860-24-2.jpg"
            alt="Jewelry crafting"
            className="w-full h-full object-cover"
          />
        </motion.div>

        {/* Values */}
        <section className="py-14 md:py-20 px-6 md:px-4">
          <div className="max-w-[90vw] md:max-w-[min(80vw,1400px)] mx-auto">
            <motion.h3
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.6, delay: 0.4 }}
              className="text-sm md:text-base tracking-widest uppercase text-black/50 dark:text-white/50 mb-8 md:mb-10"
            >
              {t('about.values')}
            </motion.h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8 md:gap-x-16 md:gap-y-10">
              {values.map((value, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 12 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.5, delay: 0.5 + index * 0.08 }}
                >
                  <h4 className="text-lg md:text-xl font-semibold text-black dark:text-white mb-1">
                    {value.title}
                  </h4>
                  <p className="text-base md:text-lg text-black/70 dark:text-white/70 leading-relaxed">
                    {value.description}
                  </p>
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        {/* Image break — two images side by side */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.8, delay: 0.5 }}
          className="w-full grid grid-cols-2 h-[30vh] md:h-[45vh]"
        >
          <div className="overflow-hidden">
            <img
              src="/DSC_9010edit копія.jpg"
              alt="Model Photo X-ray earnings"
              className="w-full h-full object-cover"
            />
          </div>
          <div className="overflow-hidden">
            <img
              src="/PESCI-BRA-PLA-001-MODELO.jpeg"
              alt="Pesci Ohh Bracelet close-up"
              className="w-full h-full object-cover scale-125 object-[50%_70%]"
            />
          </div>
        </motion.div>

        {/* FAQ */}
        <section className="py-14 md:py-20 px-6 md:px-4">
          <div className="max-w-[90vw] md:max-w-[min(80vw,1400px)] mx-auto">
            <motion.h3
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.6, delay: 0.5 }}
              className="text-sm md:text-base tracking-widest uppercase text-black/50 dark:text-white/50 mb-8 md:mb-10"
            >
              {t('about.faq')}
            </motion.h3>
            <div className="max-w-3xl divide-y divide-black/10 dark:divide-white/10">
              {faqs.map((faq, index) => {
                const isOpen = openFaqIndex === index;
                return (
                  <motion.div
                    key={index}
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ duration: 0.4, delay: 0.6 + index * 0.05 }}
                  >
                    <button
                      onClick={() => setOpenFaqIndex(isOpen ? null : index)}
                      className="w-full flex justify-between items-center py-4 md:py-5 text-left group gap-4"
                    >
                      <span className="text-base md:text-lg font-medium text-black dark:text-white group-hover:text-brand-blue transition-colors">
                        {faq.question}
                      </span>
                      <FontAwesomeIcon
                        icon={faChevronDown}
                        className={`text-black/40 dark:text-white/40 text-sm flex-shrink-0 transition-transform duration-200 ${
                          isOpen ? 'rotate-180' : ''
                        }`}
                      />
                    </button>
                    <div
                      className={`overflow-hidden transition-all duration-200 ease-in-out ${
                        isOpen ? 'max-h-96 opacity-100 pb-5' : 'max-h-0 opacity-0'
                      }`}
                    >
                      <p className="text-base md:text-lg text-black/80 dark:text-white/80 leading-relaxed">
                        {faq.answer}
                      </p>
                    </div>
                  </motion.div>
                );
              })}
            </div>
          </div>
        </section>
      </div>
    </div>
    </>
  );
};

export default About;
